import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ApiService } from 'src/app/service/api.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { toast } from 'src/app/model/toast';
import { MediaService } from 'src/app/service/media.service';
import { CampagneAndOfferFctService } from 'src/app/service/campagne-and-offer-fct.service';
import { finalize } from 'rxjs/operators';
import { FileInput } from 'ngx-material-file-input';

export enum ContentType {
  STORY = 'story',
  REEL = 'reel',
  POST = 'post'
}

export interface PigePost {
  date: Date;
  picture: string;
  caption: string;
  type: ContentType;
}

export interface PigePicJson {
  date: Date;
  user: string;
  content: {
    stories: PigePost[];
    reels: PigePost[];
    posts: PigePost[];
  };
}

@Component({
  selector: 'app-pige-admin',
  templateUrl: './pige-admin.component.html',
  styleUrls: [
    './pige-admin.component.css', 
    '../../../assets/styles/table.css', 
    '../../../assets/styles/panel.css',
    '../../../assets/styles/text-title.css',
    '../../../assets/styles/button.css',
    '../../../assets/styles/body.css',
    '../../../assets/styles/dashboard.css', 
    '../../../assets/styles/campagne.css'
  ],
  providers: [MediaService]
})
export class PigeAdminComponent implements OnInit {
  ContentType = ContentType;
  
  pigeExtern: FormGroup;
  exportArrayFiles: string[] = [];
  toast = toast;
  loading = false;
  isUploading = false;
  exportFile = false;
  exportFileUP = false;
  
  picPige: PigePicJson = {
    date: new Date(),
    user: 'InfluenceLab',
    content: {
      stories: [],
      reels: [],
      posts: []
    }
  };
  
  accordionState = {
    stories: false,
    reels: false,
    posts: false
  };

  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    public picService: MediaService,
    private toastr: ToastrService,
    public fctBasic: CampagneAndOfferFctService,
    private cdRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    const defaultName = `Pige_${new Date().toISOString().split('T')[0]}`;
    this.pigeExtern = this.fb.group({
      slideName: new FormControl(defaultName),
      userName: new FormControl(this.picPige.user || 'InfluenceLab'),
      // FormControls pour les file inputs - permet de les reset facilement
      storyFile: new FormControl(null),
      reelFile: new FormControl(null),
      postFile: new FormControl(null)
    });
  }

  // ============================================
  // UPLOAD - Simplifié, ordre chronologique garanti
  // ============================================
  
  async modifPicture(event: any, contentType: ContentType): Promise<void> {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }
    
    this.isUploading = true;
    this.cdRef.detectChanges();
    
    try {
      // Convertir et trier par date de modification (ordre chronologique)
      const files = Array.from(event.target.files) as File[];
      files.sort((a, b) => a.lastModified - b.lastModified);
      
      // Upload séquentiel pour garantir l'ordre
      for (const file of files) {
        const post = await this.uploadSingleFile(file, contentType);
        if (post) {
          this.addPostToContent(post, contentType);
        }
      }
      
      this.cdRef.detectChanges();
      
    } finally {
      this.isUploading = false;
      this.cdRef.detectChanges();
    }
  }

  private async uploadSingleFile(file: File, contentType: ContentType): Promise<PigePost | null> {
    return new Promise<PigePost | null>((resolve) => {
      const extension = this.picService.getExtentionImg(file);
      if (extension === false) {
        this.toastr.error(`Format invalide pour ${file.name}`);
        resolve(null);
        return;
      }
      
      this.resizeImage(file, contentType)
        .then((resizedFile) => {
          const fd = new FormData();
          fd.append('file', resizedFile, file.name);
          
          this.apiService.postPicture(fd).subscribe({
            next: (path) => {
              resolve({
                picture: path[0], 
                caption: '', 
                date: new Date(),
                type: contentType
              });
            },
            error: (error) => {
              console.error(`Upload error for ${file.name}:`, error);
              this.toastr.error(`Échec upload: ${file.name}`);
              resolve(null);
            }
          });
        })
        .catch((error) => {
          console.error(`Resize error for ${file.name}:`, error);
          this.toastr.error(`Échec traitement: ${file.name}`);
          resolve(null);
        });
    });
  }

  private addPostToContent(post: PigePost, contentType: ContentType): void {
    switch (contentType) {
      case ContentType.STORY:
        this.picPige.content.stories.push(post);
        break;
      case ContentType.REEL:
        this.picPige.content.reels.push(post);
        break;
      case ContentType.POST:
        this.picPige.content.posts.push(post);
        break;
    }
  }

  private async resizeImage(file: File, contentType: ContentType): Promise<File> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Dimensions cibles selon le type
        const isVertical = contentType === ContentType.STORY || contentType === ContentType.REEL;
        const targetWidth = 1080;
        const targetHeight = isVertical ? 1920 : 1080;
        
        // Calcul pour préserver le ratio
        const aspectRatio = img.width / img.height;
        let newWidth = targetWidth;
        let newHeight = targetHeight;
        
        if (aspectRatio > targetWidth / targetHeight) {
          newHeight = targetWidth / aspectRatio;
        } else {
          newWidth = targetHeight * aspectRatio;
        }
        
        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx?.drawImage(img, 0, 0, newWidth, newHeight);
        
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type }));
          } else {
            reject(new Error('Failed to resize image'));
          }
        }, file.type, 0.9);
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  // ============================================
  // DELETE
  // ============================================

  deleteImgForModif(img: string, elem: PigePost): void {
    // Supprimer de l'array immédiatement
    this.removeFromArray(elem);
    this.cdRef.detectChanges();
    
    // Supprimer du serveur si l'image existe
    if (img) {
      this.apiService.deletePicture(img).subscribe({
        next: () => this.toastr.success('Image supprimée'),
        error: (err) => console.error('Delete error:', err)
      });
    }
  }

  private removeFromArray(elem: PigePost): void {
    const content = this.picPige.content;
    switch (elem.type) {
      case ContentType.STORY:
        content.stories = content.stories.filter(s => s !== elem);
        break;
      case ContentType.REEL:
        content.reels = content.reels.filter(r => r !== elem);
        break;
      case ContentType.POST:
        content.posts = content.posts.filter(p => p !== elem);
        break;
    }
  }

  // ============================================
  // CLEAR - Fix pour reset les ngx-mat-file-input
  // ============================================

  private resetFileInput(contentType: ContentType): void {
    // Reset le FormControl
    const controlName = contentType + 'File'; // storyFile, reelFile, postFile
    this.pigeExtern.controls[controlName]?.setValue(null, { emitEvent: false });
    
    // Reset l'input natif HTML
    const selector = `ngx-mat-file-input[formControlName="${controlName}"] input[type="file"]`;
    const fileInput = document.querySelector(selector) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  clearSection(contentType: ContentType): void {
    let itemsToDelete: PigePost[] = [];
    
    switch (contentType) {
      case ContentType.STORY:
        itemsToDelete = [...this.picPige.content.stories];
        this.picPige.content.stories = [];
        break;
      case ContentType.REEL:
        itemsToDelete = [...this.picPige.content.reels];
        this.picPige.content.reels = [];
        break;
      case ContentType.POST:
        itemsToDelete = [...this.picPige.content.posts];
        this.picPige.content.posts = [];
        break;
    }
    
    // Reset le file input
    this.resetFileInput(contentType);
    
    // Supprimer les images du serveur
    itemsToDelete.forEach(item => {
      if (item.picture) {
        this.apiService.deletePicture(item.picture).subscribe({
          error: (err) => console.error('Error deleting image:', err)
        });
      }
    });
    
    this.cdRef.detectChanges();
    this.toastr.success(`Section ${contentType} effacée`);
  }

  clearAll(): void {
    if (this.getTotalImages() === 0) return;
    
    const allItems: PigePost[] = [
      ...this.picPige.content.stories,
      ...this.picPige.content.reels,
      ...this.picPige.content.posts
    ];
    
    // Vider tous les tableaux
    this.picPige.content = { stories: [], reels: [], posts: [] };
    
    // Reset état export
    this.exportFile = false;
    this.exportFileUP = false;
    this.exportArrayFiles = [];
    
    // Reset tous les file inputs
    this.resetFileInput(ContentType.STORY);
    this.resetFileInput(ContentType.REEL);
    this.resetFileInput(ContentType.POST);
    
    // Supprimer du serveur
    allItems.forEach(item => {
      if (item.picture) {
        this.apiService.deletePicture(item.picture).subscribe({
          error: (err) => console.error('Error deleting image:', err)
        });
      }
    });
    
    this.cdRef.detectChanges();
    this.toastr.success('Toutes les images ont été effacées');
  }

  // ============================================
  // GENERATE
  // ============================================

  generateExpress(): void {
    if (this.getTotalImages() === 0) {
      this.toastr.warning('Ajoutez au moins une image');
      return;
    }
    
    // Valeurs par défaut
    if (!this.pigeExtern.value.slideName?.trim()) {
      this.pigeExtern.patchValue({ slideName: `Pige_${new Date().toISOString().split('T')[0]}` });
    }
    
    this.picPige.user = this.pigeExtern.value.userName?.trim() || 'InfluenceLab';
    
    this.loading = true;
    this.cdRef.detectChanges();
    
    this.apiService.exportFilePigeExternExpress(this.picPige, this.pigeExtern.value.slideName)
      .pipe(finalize(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      }))
      .subscribe({
        next: (res) => {
          if (res.url === '400') {
            this.toastr.error('Erreur lors de la création');
            return;
          }
          
          this.exportFile = true;
          this.exportFileUP = true;
          this.exportArrayFiles = ['/api/export/pptx/' + res.url];
          this.toastr.success('Présentation créée avec succès');
        },
        error: (err) => {
          console.error('Generate error:', err);
          this.toastr.error('Échec de la génération');
        }
      });
  }

  getTotalImages(): number {
    return this.picPige.content.stories.length + 
           this.picPige.content.reels.length + 
           this.picPige.content.posts.length;
  }
}